// Shared site footer — theme-aware footer that adapts to light/dark via CSS variables.
import { Link } from "react-router-dom";

import Logo from "@/components/Logo";

// Social logos — currentColor marks so they read in both light & dark (footer CSS colors them).
function XLogo() {
  return <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>;
}
function LinkedInLogo() {
  return <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>;
}
function GlobeLogo() {
  return <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="12" r="9.5" /><path d="M2.5 12h19M12 2.5a15 15 0 0 1 4 9.5 15 15 0 0 1-4 9.5 15 15 0 0 1-4-9.5 15 15 0 0 1 4-9.5z" /></svg>;
}

// `minimal` renders a slim strip — the SkillPatch logo at the left end, and the LatentForce handle
// with Privacy / Terms at the right — used on every page except the marketing landing (full footer).
export default function SiteFooter({ minimal = false }: { minimal?: boolean }) {
  if (minimal) {
    return (
      <footer className="lpf lpf-minimal">
        <style>{FOOTER_CSS}</style>
        <Link to="/" className="footer-min-logo" aria-label="SkillPatch home">
          <Logo />
        </Link>
        <div className="footer-min-links">
          <a href="https://latentforce.ai" target="_blank" rel="noreferrer">@latentforce</a>
          <Link to="/legal/privacy">Privacy</Link>
          <Link to="/legal/terms">Terms</Link>
        </div>
      </footer>
    );
  }

  return (
    <footer className="lpf">
      <style>{FOOTER_CSS}</style>
      <div className="footer-cols">
        {/* Brand + LatentForce company details (left) */}
            <div className="footer-brand">
              <div className="footer-logo"><Logo /></div>
              <span className="mono footer-tag">Verified skills</span>
              <div className="footer-company">
                <p className="mono footer-company-label">A LatentForce product</p>
                <a href="mailto:contact@latentforce.ai">contact@latentforce.ai</a>
                {/* Phone hidden from UI for now: <a href="tel:+918130391497">+91 8130391497</a> */}
                <p className="footer-addr">1st Floor, BNN Tower, 760, 16th Main Road, Banashankari Stage II, Bengaluru, Karnataka – 560070</p>
              </div>
            </div>

            {/* Link blocks — pushed to the right end, no headings */}
            <div className="footer-links">
              <div className="footer-col">
                <Link to="/skills">Explore</Link><Link to="/docs">Docs</Link><Link to="/login">Login</Link>
              </div>
              <div className="footer-col">
                <Link to="/contact">Contact us</Link>
                <a href="https://x.com/LatentForceHQ" target="_blank" rel="noreferrer" className="footer-social-link"><XLogo /> X</a>
                <a href="https://in.linkedin.com/company/latentforce-ai" target="_blank" rel="noreferrer" className="footer-social-link"><LinkedInLogo /> LinkedIn</a>
                <a href="https://latentforce.ai" target="_blank" rel="noreferrer" className="footer-social-link"><GlobeLogo /> LatentForce</a>
              </div>
            </div>
      </div>
      <div className="footer-divider" />
      <div className="legal-row">
        <span>© 2026 LatentForce</span>
        <Link to="/legal/privacy">Privacy</Link>
        <Link to="/legal/terms">Terms</Link>
      </div>
    </footer>
  );
}

const FOOTER_CSS = `
.lpf{
  position:relative; padding:64px 72px 28px;
  background:var(--bg-subtle);
  border-top:1px solid var(--border);
  font-family:'Poppins',sans-serif; font-weight:300; color:var(--text-muted);
}
.lpf::before{content:'';position:absolute;top:0;left:0;right:0;height:1.5px;background:linear-gradient(90deg,transparent,rgba(225,100,46,.3),transparent);}
/* Slim strip on every non-landing page: logo at the left end, handle + legal at the right. */
.lpf-minimal{padding:16px 28px;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;}
.lpf-minimal .footer-min-logo{display:inline-flex;align-items:center;font-family:'Space Grotesk',sans-serif;font-weight:500;font-size:16px;color:var(--text);}
.lpf-minimal .footer-min-logo:hover{color:var(--text);}
.lpf-minimal .footer-min-links{display:flex;align-items:center;gap:20px;font-family:'Space Mono',monospace;font-size:11.5px;color:var(--text-muted);}
@media(max-width:660px){.lpf-minimal{justify-content:center;text-align:center;}}
.lpf .mono{font-family:'Space Mono',monospace;}
.lpf a{color:var(--text-muted);text-decoration:none;transition:color .15s;}
.lpf a:hover{color:var(--text);}
.lpf .footer-cols{display:flex;align-items:flex-start;justify-content:space-between;gap:60px;margin-bottom:40px;flex-wrap:wrap;}
.lpf .footer-links{display:flex;gap:64px;}
.lpf .footer-col{min-width:120px;display:flex;flex-direction:column;gap:14px;}
.lpf .footer-col a{font-size:13px;}
/* Social links: orange logo beside the label, no border, stacked with the other footer links. */
.lpf .footer-social-link{display:inline-flex;align-items:center;gap:8px;font-size:13px;}
.lpf .footer-social-link svg{color:var(--brand);flex-shrink:0;}
.lpf .footer-brand{display:flex;flex-direction:column;gap:4px;max-width:360px;}
.lpf .footer-logo{font-family:'Space Grotesk',sans-serif;font-weight:500;font-size:18px;color:var(--text);}
.lpf .footer-logo span{color:var(--brand);}
.lpf .footer-tag{font-size:10.5px;color:var(--text-muted);opacity:0.7;}
.lpf .footer-company{display:flex;flex-direction:column;gap:6px;margin-top:18px;}
.lpf .footer-company-label{font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:var(--brand);}
.lpf .footer-company a{font-size:13px;}
.lpf .footer-addr{font-size:12px;line-height:1.6;color:var(--text-muted);opacity:0.85;max-width:320px;}
.lpf .footer-divider{height:1px;background:var(--border);margin-bottom:20px;}
.lpf .legal-row{display:flex;align-items:center;justify-content:center;gap:20px;font-family:'Space Mono',monospace;font-size:10.5px;color:var(--text-muted);opacity:0.7;flex-wrap:wrap;}
@media(max-width:660px){
  .lpf{padding:56px 24px 24px;text-align:center;}
  .lpf .footer-cols{flex-direction:column;align-items:center;gap:32px;}
  .lpf .footer-brand{align-items:center;max-width:none;}
  .lpf .footer-addr{max-width:320px;margin-inline:auto;}
  .lpf .footer-links{justify-content:center;gap:56px;}
  .lpf .footer-col{align-items:center;}
  .lpf .legal-row{justify-content:center;}
}
`;
